import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Stars, Moon, Sun, AlertTriangle, Sparkles, Calendar, 
  MessageCircle, Check, TrendingUp, Heart, Send, X, User, Bot, Menu,
  Briefcase, HeartHandshake, Activity, GraduationCap, Wallet, Loader2, BookOpen,
  ChevronDown, ChevronUp
} from 'lucide-react';
import LagnaChart from '../components/LagnaChart';

const MENU_ITEMS = [
  { id: 'chart', label: 'Charts', icon: Stars },
  { id: 'life_predictions', label: 'Life Predictions', icon: BookOpen },
  { id: 'varshaphal', label: 'Annual Forecast', icon: Calendar },
  { id: 'planets', label: 'Planetary Positions', icon: Sun },
  { id: 'dasha', label: 'Current Dasha', icon: Calendar },
  { id: 'predictions', label: 'Transit Predictions', icon: AlertTriangle },
  { id: 'doshas', label: 'Doshas & Remedies', icon: AlertTriangle },
  { id: 'lunar', label: 'Past Life & Karma', icon: Moon },
  { id: 'chat', label: 'AI Chat', icon: MessageCircle }
];

function AstrologyDashboard() {
  const [kundaliData, setKundaliData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activePanel, setActivePanel] = useState('chart');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [lifePredictions, setLifePredictions] = useState(null);
  const [lifePredLoading, setLifePredLoading] = useState(false);
  const [openDivChart, setOpenDivChart] = useState('D1');
  const [lifeReportTab, setLifeReportTab] = useState('core');
  const [openHouseAccordion, setOpenHouseAccordion] = useState(1);
  const chatEndRef = useRef(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    generateKundali();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const generateKundali = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/astrology/kundali');
      setKundaliData(response.data);
      // Try to load cached life predictions
      try {
        const predRes = await axios.get('/api/astrology/life-predictions');
        if (predRes.data.status === 'completed') {
          setLifePredictions(predRes.data);
          setLifeReportTab('core');
        } else if (predRes.data.status === 'generating') {
          setLifePredLoading(true);
          pollForPredictions();
        }
      } catch (e) { /* No cached predictions yet */ }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate Kundali. Please complete your profile with birth time.');
    } finally {
      setLoading(false);
    }
  };

  const generateLifePredictions = async () => {
    setLifePredLoading(true);
    try {
      await axios.post('/api/astrology/life-predictions');
      pollForPredictions();
    } catch (err) {
      console.error('Life prediction error:', err);
      setLifePredLoading(false);
    }
  };

  const pollForPredictions = () => {
    let pollCount = 0;
    const maxPolls = 72; // 72 * 10s = 12 minutes max
    
    const interval = setInterval(async () => {
      pollCount++;
      try {
        const res = await axios.get('/api/astrology/life-predictions');
        if (res.data.status === 'completed') {
          setLifePredictions(res.data);
          setLifeReportTab('core');
          setLifePredLoading(false);
          clearInterval(interval);
        } else if (res.data.status === 'error') {
          console.error('Prediction error:', res.data.message);
          setLifePredLoading(false);
          clearInterval(interval);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Poll error:', err);
        }
      }
      
      if (pollCount >= maxPolls) {
        clearInterval(interval);
        // One final check before giving up
        try {
          const finalRes = await axios.get('/api/astrology/life-predictions');
          if (finalRes.data.status === 'completed') {
            setLifePredictions(finalRes.data);
            setLifeReportTab('core');
          }
        } catch (e) {}
        setLifePredLoading(false);
      }
    }, 10000);
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);

    try {
      const response = await axios.post('/api/astrology/chat', { message: userMsg });
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Error: ' + (err.response?.data?.detail || 'Please try again.')
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    if (severity === 'major' || severity === 'critical') return '#DC2626';
    if (severity === 'moderate') return '#FB923C';
    return '#10B981';
  };

  const getRashiColor = (rashiNum) => {
    const colors = ['#DC2626', '#EA580C', '#F59E0B', '#10B981', '#059669', '#0891B2', 
                    '#0284C7', '#7C3AED', '#A855F7', '#EC4899', '#BE123C', '#9CA3AF'];
    return colors[(rashiNum - 1) % 12] || '#9CA3AF';
  };

  const sanitizePredictionText = (text) => {
    if (!text || typeof text !== 'string') return '';

    return text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && !line.toUpperCase().startsWith('KB SIGNAL'))
      .map((line) => line
        .replace(/KB\[\d+\]/gi, '')
        .replace(/\[\d+\]/g, '')
        .replace(/\*\*/g, '')
        .replace(/^#{1,3}\s+/g, '')
        .replace(/^---+$/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim()
      )
      .filter((line) => Boolean(line) && !line.match(/^={3,}/) && !line.match(/^\(\d+\.?\d*\/10\)$/))
      .join('\n');
  };

  const buildDivisionalKundali = (chartKey) => {
    const chart = lifePredictions?.divisional_charts?.[chartKey];
    if (!chart || !kundali) return null;

    const planetary_positions = {};
    (chart.planets || []).forEach((planetData) => {
      planetary_positions[planetData.planet] = {
        rashi_num: planetData.sign_num,
        rashi: planetData.sign,
        degree_in_rashi: planetData.degree || 0,
      };
    });

    return {
      planetary_positions,
      ascendant: kundali.ascendant,
      sun_sign: kundali.sun_sign,
      moon_sign: kundali.moon_sign,
      birth_nakshatra: kundali.birth_nakshatra,
    };
  };

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
            <Stars size={64} color="#7C3AED" />
          </motion.div>
          <p style={{ fontSize: '1.25rem', color: '#9CA3AF', marginTop: '1.5rem' }}>Calculating your Kundali...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="container" style={{ maxWidth: '600px', textAlign: 'center', paddingTop: '4rem' }}>
          <div className="alert alert-warning" style={{ marginBottom: '2rem' }}>{error}</div>
          <button onClick={() => navigate('/data-input')} className="btn btn-primary">
            Complete Profile with Birth Time
          </button>
        </div>
      </div>
    );
  }

  if (!kundaliData) return null;

  const { kundali, current_dasha, predictions, lunar_astrology, doshas, planetary_interpretations } = kundaliData;

  // Render panel content based on activePanel
  const renderPanelContent = () => {
    switch (activePanel) {
      case 'chart':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Charts</h2>
            <LagnaChart kundali={kundali} />
            
            <div style={{ marginTop: '3rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div style={{ padding: '1.5rem', background: '#1E2139', borderRadius: '12px' }}>
                <Sun size={32} color="#F59E0B" style={{ marginBottom: '0.75rem' }} />
                <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>Sun Sign</p>
                <p style={{ fontSize: '1.25rem', fontWeight: 600 }}>{kundali.sun_sign}</p>
              </div>
              <div style={{ padding: '1.5rem', background: '#1E2139', borderRadius: '12px' }}>
                <Moon size={32} color="#00D9FF" style={{ marginBottom: '0.75rem' }} />
                <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>Moon Sign</p>
                <p style={{ fontSize: '1.25rem', fontWeight: 600 }}>{kundali.moon_sign}</p>
              </div>
              <div style={{ padding: '1.5rem', background: '#1E2139', borderRadius: '12px' }}>
                <Stars size={32} color="#10B981" style={{ marginBottom: '0.75rem' }} />
                <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>Ascendant</p>
                <p style={{ fontSize: '1.25rem', fontWeight: 600 }}>{kundali.ascendant.rashi}</p>
              </div>
              <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(124, 58, 237, 0.05) 100%)', borderRadius: '12px', border: '2px solid #7C3AED' }}>
                <Sparkles size={32} color="#7C3AED" style={{ marginBottom: '0.75rem' }} />
                <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>Birth Nakshatra</p>
                <p style={{ fontSize: '1.25rem', fontWeight: 700, color: '#7C3AED' }}>{kundali.birth_nakshatra}</p>
                <p style={{ fontSize: '0.875rem', color: '#A855F7', marginTop: '0.5rem' }}>
                  Pada {kundali.planetary_positions.Moon.pada}
                </p>
              </div>
            </div>

            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Divisional Charts (Traditional Houses View)</h3>
              <p style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '1rem' }}>
                All D-charts are available here in accordion style. Open any chart to view it in traditional Lagna house format.
              </p>

              {!lifePredictions?.divisional_charts && (
                <div data-testid="load-divisional-charts-card" style={{ padding: '1rem', borderRadius: '10px', background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                  <p style={{ color: '#DBEAFE', fontSize: '0.9rem', marginBottom: '0.6rem' }}>
                    Generate Life Prediction once to load all divisional charts.
                  </p>
                  <button
                    onClick={generateLifePredictions}
                    data-testid="generate-divisional-charts-button"
                    className="btn btn-primary"
                    style={{ fontSize: '0.875rem' }}
                  >
                    Generate & Load Charts
                  </button>
                </div>
              )}

              {lifePredictions?.divisional_charts && (
                <div data-testid="charts-accordion" style={{ marginTop: '0.5rem' }}>
                  {(lifePredictions.divisional_order || Object.keys(lifePredictions.divisional_charts)).map((chartKey) => {
                    const chartData = lifePredictions.divisional_charts[chartKey];
                    if (!chartData) return null;
                    const isOpen = openDivChart === chartKey;
                    const chartKundali = buildDivisionalKundali(chartKey);

                    return (
                      <div key={chartKey} style={{ marginBottom: '0.85rem', borderRadius: '12px', border: '1px solid rgba(167, 139, 250, 0.35)', background: 'rgba(167, 139, 250, 0.06)', overflow: 'hidden' }}>
                        <button
                          onClick={() => setOpenDivChart(isOpen ? '' : chartKey)}
                          data-testid={`charts-toggle-${chartKey.toLowerCase()}`}
                          style={{ width: '100%', background: 'transparent', border: 'none', padding: '0.9rem 1rem', color: '#EDE9FE', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                        >
                          <div style={{ textAlign: 'left' }}>
                            <p style={{ fontWeight: 700, fontSize: '1rem' }}>{chartKey}</p>
                            <p style={{ color: '#C4B5FD', fontSize: '0.78rem' }}>{chartData.purpose}</p>
                          </div>
                          {isOpen ? <ChevronUp size={18} color="#C4B5FD" /> : <ChevronDown size={18} color="#C4B5FD" />}
                        </button>

                        {isOpen && (
                          <div data-testid={`charts-content-${chartKey.toLowerCase()}`} style={{ padding: '0.75rem 1rem 1rem' }}>
                            {chartKundali ? (
                              <LagnaChart kundali={chartKundali} />
                            ) : (
                              <p style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>Chart data unavailable.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case 'life_predictions':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Detailed Life Predictions</h2>
            <p style={{ color: '#9CA3AF', marginBottom: '2rem', fontSize: '1rem' }}>
              Deep predictions based on your Kundali, Dasha periods, and classical astrology principles
            </p>

            {!lifePredictions && !lifePredLoading && (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <BookOpen size={64} color="#7C3AED" style={{ margin: '0 auto 1.5rem' }} />
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Generate Your Life Report</h3>
                <p style={{ color: '#9CA3AF', marginBottom: '2rem', maxWidth: '500px', margin: '0 auto 2rem' }}>
                  Get clear, actionable life predictions using divisional charts (D1, D9, D10 and more), date-based Dasha windows, and practical correction guidance.
                </p>
                <button
                  onClick={generateLifePredictions}
                  className="btn btn-primary"
                  data-testid="generate-life-predictions"
                  style={{ fontSize: '1.125rem', padding: '1rem 2rem' }}
                >
                  Generate Life Predictions
                </button>
                <p style={{ color: '#6B7280', fontSize: '0.8125rem', marginTop: '1rem' }}>
                  Takes 2-3 minutes. Your report is cached after first generation.
                </p>
              </div>
            )}

            {lifePredLoading && (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                  <Loader2 size={48} color="#7C3AED" />
                </motion.div>
                <p style={{ color: '#D1D5DB', marginTop: '1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>
                  Generating Your Life Report...
                </p>
                <p style={{ color: '#9CA3AF', fontSize: '0.9375rem', marginTop: '0.75rem' }}>
                  Reviewing your chart patterns, dasha timelines, and remedy layers
                </p>
                {/* Progress steps */}
                <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '350px', margin: '2rem auto 0' }}>
                  {['Career + Finance + Education', 'Marriage + Health + Spiritual', 'Annual Forecast 2026'].map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: '#151827', borderRadius: '8px' }}>
                      <motion.div
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.5 }}
                        style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#7C3AED' }}
                      />
                      <span style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>{step}</span>
                    </div>
                  ))}
                </div>
                <p style={{ color: '#6B7280', fontSize: '0.8125rem', marginTop: '1.5rem' }}>
                  Takes 2-3 minutes. Please wait...
                </p>
              </div>
            )}

            {lifePredictions && !lifePredLoading && (
              <div style={{ display: 'grid', gap: '2rem' }}>
                <div data-testid="life-report-tabs" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.55rem' }}>
                  {[
                    { id: 'core', label: 'Core' },
                    { id: 'planetary', label: 'Planetary' },
                    { id: 'houses', label: 'Houses' },
                    { id: 'divisional', label: 'D-Charts' },
                    { id: 'life_areas', label: 'Life Areas' },
                    { id: 'remedies', label: 'Remedies' },
                    { id: 'summary', label: 'Summary' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      data-testid={`life-report-tab-${tab.id}`}
                      onClick={() => setLifeReportTab(tab.id)}
                      style={{
                        border: lifeReportTab === tab.id ? '1px solid #00D9FF' : '1px solid rgba(255,255,255,0.18)',
                        background: lifeReportTab === tab.id ? 'rgba(0, 217, 255, 0.12)' : 'rgba(15, 23, 42, 0.45)',
                        color: '#E5E7EB',
                        borderRadius: '999px',
                        padding: '0.38rem 0.9rem',
                        fontSize: '0.82rem',
                        cursor: 'pointer'
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {(() => {
                  const detailed = lifePredictions.detailed_report || {};
                  const cardStyle = {
                    padding: '1.25rem',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,217,255,0.22)',
                    background: 'rgba(15, 23, 42, 0.58)'
                  };

                  if (lifeReportTab === 'core') {
                    const personal = detailed.personal_information_section || {};
                    const core = detailed.core_personality_analysis || {};
                    return (
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={cardStyle} data-testid="core-personal-information">
                          <h3 style={{ color: '#67E8F9', marginBottom: '0.6rem' }}>PERSONAL INFORMATION SECTION</h3>
                          <p style={{ color: '#E5E7EB', lineHeight: 1.75, fontSize: '0.92rem', whiteSpace: 'pre-line' }}>{personal.birth_data_summary || sanitizePredictionText(lifePredictions.personal_information_section)}</p>
                          {personal.chart_calculation_details && (
                            <p style={{ color: '#93C5FD', marginTop: '0.65rem', fontSize: '0.86rem' }}>
                              Chart Calculation: Ayanamsha {personal.chart_calculation_details.ayanamsha}, House System {personal.chart_calculation_details.house_system}, Time Zone {personal.chart_calculation_details.time_zone_reference}
                            </p>
                          )}
                        </div>
                        <div style={cardStyle} data-testid="core-personality-analysis">
                          <h3 style={{ color: '#A7F3D0', marginBottom: '0.6rem' }}>CORE PERSONALITY ANALYSIS</h3>
                          {Object.entries(core).map(([k, v]) => (
                            <p key={k} style={{ color: '#D1D5DB', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '0.5rem' }}>{v}</p>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  if (lifeReportTab === 'planetary') {
                    const p = detailed.planetary_analysis_detailed || {};
                    return (
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        <div style={cardStyle} data-testid="planetary-analysis-detailed">
                          <h3 style={{ color: '#C4B5FD', marginBottom: '0.6rem' }}>PLANETARY ANALYSIS (DETAILED)</h3>
                          {[
                            ['Sun – Placement & Meaning', p.sun_placement_meaning],
                            ['Moon – Emotional Pattern', p.moon_emotional_pattern],
                            ['Mars – Energy & Action', p.mars_energy_action],
                            ['Mercury – Communication & Intelligence', p.mercury_communication_intelligence],
                            ['Jupiter – Wisdom & Expansion', p.jupiter_wisdom_expansion],
                            ['Venus – Love & Aesthetics', p.venus_love_aesthetics],
                            ['Saturn – Discipline & Karma', p.saturn_discipline_karma],
                            ['Rahu – Ambition & Obsession', p.rahu_ambition_obsession],
                            ['Ketu – Detachment & Spiritual Themes', p.ketu_detachment_spiritual],
                          ].map(([h, text], idx) => (
                            <div key={idx} style={{ marginBottom: '0.45rem' }}>
                              <p style={{ color: '#EDE9FE', fontWeight: 700, fontSize: '0.88rem' }}>{h}</p>
                              <p style={{ color: '#D1D5DB', fontSize: '0.9rem', lineHeight: 1.65 }}>{text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  if (lifeReportTab === 'houses') {
                    const hData = detailed.house_analysis || {};
                    return (
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        <h3 style={{ color: '#67E8F9' }}>HOUSE ANALYSIS (1-12)</h3>
                        {(hData.houses || []).map((house) => {
                          const isOpen = openHouseAccordion === house.house;
                          return (
                            <div key={house.house} style={{ border: '1px solid rgba(103,232,249,0.25)', borderRadius: '10px', overflow: 'hidden', background: 'rgba(15,23,42,0.48)' }}>
                              <button
                                onClick={() => setOpenHouseAccordion(isOpen ? 0 : house.house)}
                                data-testid={`house-toggle-${house.house}`}
                                style={{ width: '100%', background: 'transparent', border: 'none', color: '#E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0.95rem', cursor: 'pointer' }}
                              >
                                <span>{house.house}th House - {house.title}</span>
                                {isOpen ? <ChevronUp size={16} color="#67E8F9" /> : <ChevronDown size={16} color="#67E8F9" />}
                              </button>
                              {isOpen && (
                                <div style={{ padding: '0.75rem 0.95rem' }} data-testid={`house-content-${house.house}`}>
                                  <p style={{ color: '#D1D5DB', fontSize: '0.88rem', lineHeight: 1.65 }}>{house.summary}</p>
                                  <p style={{ color: '#93C5FD', fontSize: '0.84rem', marginTop: '0.35rem' }}>Sign: {house.sign} | Lord: {house.lord} | Planets: {(house.resident_planets || []).join(', ') || 'none'}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }

                  if (lifeReportTab === 'divisional') {
                    const d = detailed.divisional_chart_analysis || {};
                    return (
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        <h3 style={{ color: '#C4B5FD' }}>DIVISIONAL CHART ANALYSIS</h3>
                        {[
                          ['Navamsa (D9) – Marriage & Dharma', d.d9_navamsa],
                          ['Dashamsa (D10) – Career', d.d10_dashamsa],
                          ['Saptamsa (D7) – Children', d.d7_saptamsa],
                          ['Shashtamsa (D6) – Health', d.d6_shashtamsa],
                          ['Chaturthamsa (D4) – Property', d.d4_chaturthamsa],
                          ['Shashtiamsa (D60) – Karmic Patterns', d.d60_shashtiamsa]
                        ].map(([head, text], idx) => (
                          <div key={idx} style={cardStyle} data-testid={`dchart-analysis-${idx}`}>
                            <p style={{ color: '#DDD6FE', fontWeight: 700, marginBottom: '0.3rem', fontSize: '0.9rem' }}>{head}</p>
                            <p style={{ color: '#E5E7EB', lineHeight: 1.7, fontSize: '0.9rem' }}>{text}</p>
                          </div>
                        ))}
                      </div>
                    );
                  }

                  if (lifeReportTab === 'life_areas') {
                    const l = detailed.life_area_reports || {};
                    const areaConfig = [
                      { key: 'health_report', title: 'Health Report', icon: Activity, color: '#EF4444', bgColor: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.35)' },
                      { key: 'career_report', title: 'Career Report', icon: Briefcase, color: '#3B82F6', bgColor: 'rgba(59,130,246,0.08)', borderColor: 'rgba(59,130,246,0.35)' },
                      { key: 'finance_report', title: 'Finance Report', icon: Wallet, color: '#F59E0B', bgColor: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' },
                      { key: 'relationship_report', title: 'Relationship Report', icon: Heart, color: '#EC4899', bgColor: 'rgba(236,72,153,0.08)', borderColor: 'rgba(236,72,153,0.35)' },
                      { key: 'marriage_report', title: 'Marriage Report', icon: HeartHandshake, color: '#A855F7', bgColor: 'rgba(168,85,247,0.08)', borderColor: 'rgba(168,85,247,0.35)' },
                      { key: 'children_report', title: 'Children Report', icon: Stars, color: '#10B981', bgColor: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.35)' },
                      { key: 'foreign_settlement_report', title: 'Foreign Settlement Report', icon: TrendingUp, color: '#06B6D4', bgColor: 'rgba(6,182,212,0.08)', borderColor: 'rgba(6,182,212,0.35)' },
                      { key: 'spiritual_path', title: 'Spiritual Path', icon: Moon, color: '#8B5CF6', bgColor: 'rgba(139,92,246,0.08)', borderColor: 'rgba(139,92,246,0.35)' },
                    ];

                    const scoreBarColor = (score) => {
                      if (score >= 7.5) return '#10B981';
                      if (score >= 5.5) return '#F59E0B';
                      if (score >= 3.5) return '#FB923C';
                      return '#EF4444';
                    };

                    return (
                      <div style={{ display: 'grid', gap: '1.25rem' }}>
                        <h3 style={{ color: '#A7F3D0', fontSize: '1.35rem' }}>LIFE AREA REPORTS</h3>
                        {areaConfig.map(({ key, title, icon: Icon, color, bgColor, borderColor }) => {
                          const area = l[key];
                          if (!area) return null;
                          const sections = area.sections || {};
                          const score = area.score !== undefined ? area.score : (area.vitality_score || area.career_strength_score || area.wealth_potential_score || area.marital_stability_index);
                          const scoreLabel = area.score_label || '';
                          const timingWindows = area.timing_windows || [];

                          // Fallback for old format: if no sections, use the area's direct keys as sections
                          const displaySections = Object.keys(sections).length > 0
                            ? sections
                            : Object.fromEntries(
                                Object.entries(area)
                                  .filter(([k]) => !['score', 'score_label', 'timing_windows'].includes(k))
                                  .map(([k, v]) => [k, Array.isArray(v) ? v.map(x => typeof x === 'object' ? `${x.planet}: ${x.start} → ${x.end}` : x).join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v))])
                              );

                          return (
                            <div key={key} data-testid={`life-area-report-${key}`} style={{
                              borderRadius: '16px',
                              border: `1px solid ${borderColor}`,
                              background: bgColor,
                              overflow: 'hidden',
                            }}>
                              {/* Header */}
                              <div style={{
                                padding: '1.25rem 1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                borderBottom: `1px solid ${borderColor}`,
                              }}>
                                <div style={{
                                  width: '44px', height: '44px', borderRadius: '12px',
                                  background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <Icon size={24} color={color} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <h4 data-testid={`life-area-title-${key}`} style={{ fontSize: '1.1rem', fontWeight: 700, color: '#F1F5F9', margin: 0 }}>{title}</h4>
                                  {score !== undefined && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.4rem' }}>
                                      <div style={{ flex: 1, maxWidth: '140px', height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.1)' }}>
                                        <div style={{ width: `${Math.min(score * 10, 100)}%`, height: '100%', borderRadius: '3px', background: scoreBarColor(score), transition: 'width 0.3s' }} />
                                      </div>
                                      <span data-testid={`life-area-score-${key}`} style={{ fontSize: '0.82rem', color: scoreBarColor(score), fontWeight: 700 }}>{score}/10 · {scoreLabel}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Content sections */}
                              <div style={{ padding: '1.25rem 1.5rem', display: 'grid', gap: '1.1rem' }}>
                                {Object.entries(displaySections).map(([subKey, text]) => {
                                  if (!text) return null;
                                  const subTitle = subKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/And/g, '&');
                                  return (
                                    <div key={subKey} data-testid={`life-area-sub-${key}-${subKey}`}>
                                      <p style={{ color, fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{subTitle}</p>
                                      <p style={{ color: '#D1D5DB', fontSize: '0.92rem', lineHeight: 1.78, whiteSpace: 'pre-line' }}>{sanitizePredictionText(text)}</p>
                                    </div>
                                  );
                                })}

                                {/* Timing Windows */}
                                {timingWindows.length > 0 && (
                                  <div style={{ marginTop: '0.35rem', padding: '0.85rem 1rem', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                    <p style={{ color: '#9CA3AF', fontWeight: 700, fontSize: '0.78rem', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Key Timing Windows</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                      {timingWindows.map((w, idx) => (
                                        <span key={idx} style={{
                                          padding: '0.3rem 0.7rem', borderRadius: '6px', fontSize: '0.8rem',
                                          background: `${color}18`, color: color, border: `1px solid ${color}40`,
                                        }}>
                                          {w.planet}: {w.start} → {w.end}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }

                  if (lifeReportTab === 'remedies') {
                    const r = detailed.remedies_recommendations || {};
                    const gemstones = r.gemstone_suggestions || [];
                    const metals = r.metal_suggestions || [];
                    const charities = r.charity_suggestions || [];
                    const lifestyle = r.lifestyle_adjustments || [];
                    const healthAdv = r.preventive_health_advisory || [];
                    const foodRem = r.food_remedies || {};
                    const careerTips = r.career_enhancement_suggestions || [];
                    const relTips = r.relationship_harmony_practices || [];
                    const sadeSatiRem = r.sade_sati_remedies || [];

                    const priorityColor = (p) => p === 'High' ? '#EF4444' : '#F59E0B';

                    return (
                      <div style={{ display: 'grid', gap: '1.1rem' }}>
                        <h3 style={{ color: '#FCD34D', fontSize: '1.35rem' }}>REMEDIES & RECOMMENDATIONS</h3>

                        {/* Sade Sati Alert */}
                        {sadeSatiRem.length > 0 && (
                          <div data-testid="remedy-sade-sati" style={{ padding: '1.25rem', borderRadius: '14px', border: '2px solid #DC2626', background: 'rgba(220,38,38,0.1)' }}>
                            <p style={{ color: '#FCA5A5', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.6rem' }}>SADE SATI ACTIVE — PRIORITY REMEDIES</p>
                            <ul style={{ margin: 0, paddingLeft: '1rem', color: '#FDE68A', fontSize: '0.9rem', lineHeight: 1.8 }}>
                              {sadeSatiRem.map((item, idx) => <li key={idx}>{item}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Gemstone Suggestions */}
                        <div data-testid="remedy-block-gemstone_suggestions" style={{ padding: '1.25rem', borderRadius: '14px', border: '1px solid rgba(168,85,247,0.35)', background: 'rgba(168,85,247,0.06)' }}>
                          <p style={{ color: '#C4B5FD', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.8rem' }}>GEMSTONE SUGGESTIONS</p>
                          {gemstones.length > 0 ? (
                            <div style={{ display: 'grid', gap: '0.85rem' }}>
                              {gemstones.map((g, idx) => (
                                <div key={idx} style={{ padding: '0.85rem 1rem', borderRadius: '10px', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                                    <span style={{ color: '#EDE9FE', fontWeight: 700, fontSize: '0.95rem' }}>{g.gemstone}</span>
                                    <span style={{ color: priorityColor(g.priority), fontSize: '0.75rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: '4px', background: `${priorityColor(g.priority)}18` }}>{g.priority}</span>
                                  </div>
                                  <p style={{ color: '#D1D5DB', fontSize: '0.85rem', marginBottom: '0.25rem' }}>For <strong style={{ color: '#C4B5FD' }}>{g.planet}</strong> — {g.reason}</p>
                                  <p style={{ color: '#9CA3AF', fontSize: '0.82rem' }}>Weight: {g.weight} | Set in: {g.metal_setting} | Finger: {g.finger} | Wear on: {g.wearing_day}</p>
                                  {g.alternatives && <p style={{ color: '#7C3AED', fontSize: '0.8rem', marginTop: '0.2rem' }}>Alternatives: {g.alternatives.join(', ')}</p>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ color: '#9CA3AF', fontSize: '0.88rem' }}>All planets are well-placed. No urgent gemstone correction needed.</p>
                          )}
                        </div>

                        {/* Metal Suggestions */}
                        <div data-testid="remedy-block-metal_suggestions" style={{ padding: '1.25rem', borderRadius: '14px', border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.06)' }}>
                          <p style={{ color: '#FDE68A', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.8rem' }}>METAL SUGGESTIONS</p>
                          {metals.length > 0 ? (
                            <div style={{ display: 'grid', gap: '0.7rem' }}>
                              {metals.map((m, idx) => (
                                <div key={idx} style={{ padding: '0.75rem 1rem', borderRadius: '10px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.3rem' }}>
                                    <span style={{ color: '#FBBF24', fontWeight: 700, fontSize: '0.95rem' }}>{m.metal}</span>
                                    <span style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>for {m.planet}</span>
                                  </div>
                                  <p style={{ color: '#D1D5DB', fontSize: '0.85rem' }}>{m.usage}</p>
                                  <p style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>{m.reason} | Donate on: {m.donation_day}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ color: '#9CA3AF', fontSize: '0.88rem' }}>No specific metal correction needed at this time.</p>
                          )}
                        </div>

                        {/* Charity Suggestions - Phase Wise */}
                        <div data-testid="remedy-block-charity_suggestions" style={{ padding: '1.25rem', borderRadius: '14px', border: '1px solid rgba(16,185,129,0.35)', background: 'rgba(16,185,129,0.06)' }}>
                          <p style={{ color: '#6EE7B7', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.8rem' }}>CHARITY SUGGESTIONS (PHASE-WISE)</p>
                          {charities.length > 0 ? (
                            <div style={{ display: 'grid', gap: '0.7rem' }}>
                              {charities.map((c, idx) => (
                                <div key={idx} style={{ padding: '0.75rem 1rem', borderRadius: '10px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                                    <span style={{ color: '#A7F3D0', fontWeight: 700, fontSize: '0.92rem' }}>{c.planet} Remedy</span>
                                    <span style={{ color: '#10B981', fontSize: '0.75rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '4px', background: 'rgba(16,185,129,0.15)' }}>{c.phase}</span>
                                  </div>
                                  <p style={{ color: '#D1D5DB', fontSize: '0.85rem' }}>Donate: <strong>{(c.items || []).join(', ')}</strong></p>
                                  <p style={{ color: '#9CA3AF', fontSize: '0.82rem' }}>Day: {c.day} | To: {c.to_whom}</p>
                                  <p style={{ color: '#86EFAC', fontSize: '0.82rem', marginTop: '0.15rem' }}>{c.food_donation}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ color: '#9CA3AF', fontSize: '0.88rem' }}>No specific charity correction needed at this time.</p>
                          )}
                        </div>

                        {/* Food Remedies */}
                        {(foodRem.eat?.length > 0 || foodRem.avoid?.length > 0) && (
                          <div data-testid="remedy-block-food_remedies" style={{ padding: '1.25rem', borderRadius: '14px', border: '1px solid rgba(251,146,60,0.35)', background: 'rgba(251,146,60,0.06)' }}>
                            <p style={{ color: '#FDBA74', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.6rem' }}>FOOD REMEDIES</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div>
                                <p style={{ color: '#10B981', fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.3rem' }}>RECOMMENDED</p>
                                <ul style={{ margin: 0, paddingLeft: '1rem', color: '#D1D5DB', fontSize: '0.85rem', lineHeight: 1.75 }}>
                                  {(foodRem.eat || []).map((item, idx) => <li key={idx}>{item}</li>)}
                                </ul>
                              </div>
                              <div>
                                <p style={{ color: '#EF4444', fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.3rem' }}>AVOID</p>
                                <ul style={{ margin: 0, paddingLeft: '1rem', color: '#D1D5DB', fontSize: '0.85rem', lineHeight: 1.75 }}>
                                  {(foodRem.avoid || []).map((item, idx) => <li key={idx}>{item}</li>)}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Preventive Health Advisory */}
                        {healthAdv.length > 0 && (
                          <div data-testid="remedy-block-health_advisory" style={{ padding: '1.25rem', borderRadius: '14px', border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.05)' }}>
                            <p style={{ color: '#FCA5A5', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.6rem' }}>PREVENTIVE HEALTH ADVISORY</p>
                            <div style={{ display: 'grid', gap: '0.6rem' }}>
                              {healthAdv.map((h, idx) => (
                                <div key={idx} style={{ padding: '0.65rem 0.85rem', borderRadius: '8px', background: 'rgba(239,68,68,0.04)', borderLeft: '3px solid #EF4444' }}>
                                  <p style={{ color: '#FCA5A5', fontWeight: 600, fontSize: '0.82rem' }}>{typeof h === 'object' ? h.planet : ''} — {typeof h === 'object' ? h.body_parts : h}</p>
                                  <p style={{ color: '#D1D5DB', fontSize: '0.85rem' }}>{typeof h === 'object' ? h.remedy : ''}</p>
                                  {typeof h === 'object' && h.color_therapy && <p style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Color therapy: Wear {h.color_therapy}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Lifestyle + Career + Relationship */}
                        {lifestyle.length > 0 && (
                          <div data-testid="remedy-block-lifestyle" style={cardStyle}>
                            <p style={{ color: '#93C5FD', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>LIFESTYLE ADJUSTMENTS</p>
                            <ul style={{ margin: 0, paddingLeft: '1rem', color: '#D1D5DB', fontSize: '0.88rem', lineHeight: 1.8 }}>
                              {lifestyle.map((item, idx) => <li key={idx}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}
                            </ul>
                          </div>
                        )}
                        {careerTips.length > 0 && (
                          <div data-testid="remedy-block-career" style={cardStyle}>
                            <p style={{ color: '#93C5FD', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>CAREER ENHANCEMENT</p>
                            <ul style={{ margin: 0, paddingLeft: '1rem', color: '#D1D5DB', fontSize: '0.88rem', lineHeight: 1.8 }}>
                              {careerTips.map((item, idx) => <li key={idx}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}
                            </ul>
                          </div>
                        )}
                        {relTips.length > 0 && (
                          <div data-testid="remedy-block-relationship" style={cardStyle}>
                            <p style={{ color: '#F9A8D4', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>RELATIONSHIP HARMONY</p>
                            <ul style={{ margin: 0, paddingLeft: '1rem', color: '#D1D5DB', fontSize: '0.88rem', lineHeight: 1.8 }}>
                              {relTips.map((item, idx) => <li key={idx}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  }

                  const s = detailed.summary_dashboard || {};
                  return (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      <h3 style={{ color: '#67E8F9' }}>SUMMARY DASHBOARD</h3>
                      <div style={cardStyle} data-testid="summary-dashboard">
                        <p style={{ color: '#E5E7EB', marginBottom: '0.4rem' }}><strong>Overall Life Strength Index:</strong> {s.overall_life_strength_index}</p>
                        <p style={{ color: '#E5E7EB', marginBottom: '0.4rem' }}><strong>Current Phase Intensity Meter:</strong> {s.current_phase_intensity_meter}</p>
                        <p style={{ color: '#E5E7EB', marginBottom: '0.4rem' }}><strong>Opportunity vs Challenge Graph:</strong> {JSON.stringify(s.opportunity_vs_challenge_graph || {})}</p>
                        <p style={{ color: '#E5E7EB', marginBottom: '0.25rem' }}><strong>5-Year Strategic Outlook:</strong></p>
                        <ul style={{ margin: 0, paddingLeft: '1rem', color: '#D1D5DB', lineHeight: 1.7 }}>
                          {(s.five_year_strategic_outlook || []).map((line, idx) => <li key={idx}>{line}</li>)}
                        </ul>
                      </div>
                    </div>
                  );
                })()}

                {/* Regenerate button */}
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={generateLifePredictions}
                    className="btn btn-ghost"
                    data-testid="regenerate-life-predictions"
                    style={{ fontSize: '0.875rem' }}
                  >
                    Regenerate Predictions
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'varshaphal':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
              Annual Forecast {lifePredictions?.year || new Date().getFullYear()}
            </h2>
            <p style={{ color: '#9CA3AF', marginBottom: '2rem', fontSize: '1rem' }}>
              Month-by-month predictions based on planetary transits and Dasha periods
            </p>

            {!lifePredictions?.varshaphal && !lifePredLoading && (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <Calendar size={64} color="#00D9FF" style={{ margin: '0 auto 1.5rem' }} />
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Generate Annual Forecast</h3>
                <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>
                  Get month-by-month predictions for career, finance, health, and relationships.
                </p>
                <button
                  onClick={generateLifePredictions}
                  className="btn btn-primary"
                  data-testid="generate-annual-forecast"
                  style={{ fontSize: '1.125rem', padding: '1rem 2rem' }}
                >
                  Generate Annual Forecast
                </button>
              </div>
            )}

            {lifePredLoading && (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                  <Loader2 size={48} color="#00D9FF" />
                </motion.div>
                <p style={{ color: '#9CA3AF', marginTop: '1.5rem' }}>Generating annual forecast...</p>
              </div>
            )}

            {lifePredictions?.varshaphal && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  padding: '2rem',
                  background: '#151827',
                  borderRadius: '16px',
                  border: '2px solid rgba(0, 217, 255, 0.3)',
                  borderLeft: '5px solid #00D9FF'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: 'rgba(0, 217, 255, 0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Calendar size={28} color="#00D9FF" />
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#00D9FF' }}>
                    Varshaphal {lifePredictions.year || new Date().getFullYear()}
                  </h3>
                </div>
                <div style={{ fontSize: '1rem', lineHeight: 1.85, color: '#D1D5DB' }}>
                  {sanitizePredictionText(lifePredictions.varshaphal).split('\n').filter(l => l.trim()).map((line, idx) => {
                    const trimmed = line.trim();
                    if (/^\d+\.\s+[A-Z]/.test(trimmed)) {
                      return <h4 key={idx} style={{ fontSize: '1.125rem', fontWeight: 700, color: '#00D9FF', marginTop: idx > 0 ? '1.5rem' : '0', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid rgba(0,217,255,0.2)' }}>{trimmed}</h4>;
                    }
                    if (/^[-*]\s/.test(trimmed)) {
                      return <div key={idx} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem', paddingLeft: '0.5rem' }}><span style={{ color: '#00D9FF', fontSize: '1.25rem', lineHeight: '1.5', flexShrink: 0 }}>&#x2022;</span><span style={{ fontSize: '0.9375rem', lineHeight: 1.7, color: '#D1D5DB' }}>{trimmed.replace(/^[-*]\s/, '')}</span></div>;
                    }
                    if (/^[A-Z][a-z]+:/.test(trimmed)) {
                      return <p key={idx} style={{ fontSize: '1rem', fontWeight: 600, color: '#E5E7EB', marginTop: '1rem', marginBottom: '0.25rem' }}>{trimmed}</p>;
                    }
                    return <p key={idx} style={{ fontSize: '0.9375rem', lineHeight: 1.75, color: '#D1D5DB', marginBottom: '0.5rem' }}>{trimmed}</p>;
                  })}
                </div>
              </motion.div>
            )}
          </div>
        );

      case 'planets':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>9 Planetary Positions (Navagraha)</h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {Object.entries(kundali.planetary_positions).map(([planet, data]) => {
                const interp = planetary_interpretations?.[planet] || {};
                const bgColor = interp.sentiment === 'positive' ? 'rgba(16, 185, 129, 0.08)' : 
                               interp.sentiment === 'concerning' ? 'rgba(220, 38, 38, 0.08)' : 
                               'rgba(245, 158, 11, 0.05)';
                const borderColor = interp.color || '#F59E0B';
                
                return (
                <div key={planet} style={{ 
                  padding: '2rem', 
                  background: bgColor,
                  borderRadius: '16px',
                  border: `3px solid ${borderColor}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.75rem' }}>{planet}</h3>
                      <p style={{ fontSize: '1.125rem', color: getRashiColor(data.rashi_num), fontWeight: 600, marginBottom: '0.75rem' }}>
                        {data.rashi}
                      </p>
                      <div style={{ 
                        display: 'inline-block',
                        padding: '0.75rem 1.25rem',
                        background: 'rgba(124, 58, 237, 0.15)',
                        borderRadius: '10px',
                        border: '2px solid #7C3AED',
                        marginBottom: '1rem'
                      }}>
                        <p style={{ fontSize: '1.125rem', fontWeight: 700, color: '#7C3AED', marginBottom: '0.25rem' }}>
                          {data.nakshatra} - Pada {data.pada}
                        </p>
                        <p style={{ fontSize: '0.875rem', color: '#A855F7' }}>
                          Lord: {data.nakshatra_lord}
                        </p>
                      </div>
                      
                      {/* Interpretation */}
                      {interp.interpretation && (
                        <div style={{ 
                          padding: '1.25rem', 
                          background: `${borderColor}15`,
                          borderRadius: '10px',
                          borderLeft: `4px solid ${borderColor}`,
                          marginBottom: '1rem'
                        }}>
                          <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#E5E7EB' }}>
                            {interp.interpretation}
                          </p>
                        </div>
                      )}
                      
                      {interp.general_meaning && (
                        <p style={{ fontSize: '0.9375rem', color: '#9CA3AF', lineHeight: 1.6 }}>
                          <strong>General Meaning:</strong> {interp.general_meaning}
                        </p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: '1.5rem' }}>
                      <p style={{ fontSize: '1.25rem', fontWeight: 600, color: '#9CA3AF', marginBottom: '0.5rem' }}>
                        {data.degree_in_rashi.toFixed(2)}°
                      </p>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </div>
        );

      case 'dasha':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Vimshottari Dasha Periods</h2>
            {current_dasha && (
              <div style={{ 
                padding: '2.5rem', 
                background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(168, 85, 247, 0.08) 100%)', 
                borderRadius: '16px',
                border: '3px solid #7C3AED',
                textAlign: 'center',
                marginBottom: '2rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.75rem' }}>CURRENTLY RUNNING</p>
                <h3 style={{ fontSize: '3rem', fontWeight: 700, color: '#7C3AED', marginBottom: '1rem' }}>
                  {current_dasha.planet} Mahadasha
                </h3>
                <p style={{ fontSize: '1.25rem', color: '#D1D5DB', marginBottom: '0.5rem' }}>
                  {current_dasha.start_date} to {current_dasha.end_date}
                </p>
                <p style={{ fontSize: '1rem', color: '#9CA3AF' }}>
                  Duration: {current_dasha.duration_years} years
                </p>
              </div>
            )}

            {kundaliData.all_dashas && (
              <div>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Upcoming Mahadashas</h3>
                {kundaliData.all_dashas.filter(d => !d.is_current).slice(0, 5).map((dasha, i) => (
                  <div key={i} style={{ 
                    padding: '1.25rem 1.5rem', 
                    background: '#1E2139', 
                    borderRadius: '12px',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <p style={{ fontSize: '1.25rem', fontWeight: 700 }}>{dasha.planet} Mahadasha</p>
                      <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
                        {dasha.start_date} to {dasha.end_date}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '1.125rem', fontWeight: 600, color: '#7C3AED' }}>
                        {dasha.duration_years} years
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'doshas':
        return doshas && doshas.length > 0 ? (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Astrological Doshas & Remedies</h2>
            <div className="alert alert-info" style={{ marginBottom: '2rem' }}>
              <strong>Note:</strong> Doshas are planetary combinations that create challenges. With proper remedies, their effects can be minimized.
            </div>
            
            {doshas.map((dosha, i) => (
              <div key={i} style={{ 
                padding: '2rem', 
                background: dosha.severity === 'major' ? 'rgba(220, 38, 38, 0.08)' : 'rgba(251, 146, 60, 0.08)',
                borderRadius: '16px',
                border: `3px solid ${dosha.severity === 'major' ? '#DC2626' : '#FB923C'}`,
                marginBottom: '2rem',
                boxShadow: dosha.severity === 'major' ? '0 4px 20px rgba(220, 38, 38, 0.3)' : 'none'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '2rem', fontWeight: 700, color: dosha.severity === 'major' ? '#DC2626' : '#FB923C' }}>
                    {dosha.name}
                  </h3>
                  <span style={{ 
                    padding: '0.75rem 1.5rem',
                    background: dosha.severity === 'major' ? 'rgba(220, 38, 38, 0.2)' : 'rgba(251, 146, 60, 0.2)',
                    color: dosha.severity === 'major' ? '#DC2626' : '#FB923C',
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase'
                  }}>
                    {dosha.severity}
                  </span>
                </div>

                <p style={{ fontSize: '1.125rem', lineHeight: 1.8, marginBottom: '2rem', padding: '1.5rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
                  {dosha.description}
                </p>

                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem', color: '#FB923C' }}>
                    Effects You May Experience:
                  </h4>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {dosha.effects.map((effect, j) => (
                      <div key={j} style={{ 
                        padding: '1rem', 
                        background: 'rgba(220, 38, 38, 0.1)', 
                        borderRadius: '8px',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        borderLeft: '3px solid #DC2626'
                      }}>
                        {effect}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ 
                  padding: '2rem', 
                  background: 'rgba(16, 185, 129, 0.1)', 
                  borderRadius: '12px',
                  border: '2px solid #10B981'
                }}>
                  <h4 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Heart size={24} />
                    Powerful Remedies to Nullify Effects
                  </h4>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {dosha.remedies.map((remedy, j) => (
                      <div key={j} style={{ 
                        padding: '1.25rem', 
                        background: 'rgba(16, 185, 129, 0.15)', 
                        borderRadius: '10px',
                        borderLeft: '4px solid #10B981'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                          <span style={{ 
                            display: 'inline-block',
                            padding: '0.5rem 1rem',
                            background: 'rgba(16, 185, 129, 0.3)',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#10B981',
                            textTransform: 'uppercase',
                            flexShrink: 0
                          }}>
                            {remedy.type}
                          </span>
                          <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#E5E7EB', margin: 0 }}>
                            {remedy.action}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <Check size={64} color="#10B981" style={{ margin: '0 auto 1.5rem' }} />
            <h3 style={{ fontSize: '1.75rem', marginBottom: '1rem', color: '#10B981' }}>
              No Major Doshas Detected!
            </h3>
            <p style={{ color: '#D1D5DB', fontSize: '1.125rem' }}>
              Your Kundali shows relatively clean planetary positions without major doshas.
            </p>
          </div>
        );

      case 'predictions':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>2-Month Advance Predictions</h2>
            {predictions && predictions.map((pred, i) => {
              const isGood = pred.severity === 'minor';
              const isDanger = pred.severity === 'major' || pred.severity === 'critical';
              const bgColor = isGood ? 'rgba(16, 185, 129, 0.08)' : 'rgba(220, 38, 38, 0.08)';
              const borderColor = getSeverityColor(pred.severity);
              
              return (
                <div key={i} style={{ 
                  padding: '2rem', 
                  background: bgColor,
                  borderRadius: '16px',
                  border: `3px solid ${borderColor}`,
                  marginBottom: '2rem',
                  boxShadow: isDanger ? '0 4px 20px rgba(220, 38, 38, 0.3)' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    {isDanger ? <AlertTriangle size={32} color="#DC2626" /> : <TrendingUp size={32} color="#10B981" />}
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>{pred.type}</h3>
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', color: '#9CA3AF' }}>
                        <span>{pred.timing}</span>
                        <span>•</span>
                        <span>{pred.duration}</span>
                      </div>
                    </div>
                    <span style={{ 
                      padding: '0.75rem 1.5rem',
                      background: isGood ? 'rgba(16, 185, 129, 0.2)' : 'rgba(220, 38, 38, 0.2)',
                      color: isGood ? '#10B981' : '#DC2626',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      {pred.severity}
                    </span>
                  </div>

                  <div style={{ 
                    padding: '1.5rem', 
                    background: isDanger ? 'rgba(220, 38, 38, 0.15)' : 'rgba(59, 130, 246, 0.1)', 
                    borderRadius: '12px', 
                    marginBottom: '1.5rem',
                    borderLeft: `4px solid ${isDanger ? '#DC2626' : '#3B82F6'}`
                  }}>
                    <p style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.75rem', color: isDanger ? '#DC2626' : '#3B82F6', textTransform: 'uppercase' }}>
                      {isDanger ? '⚠️ Effects to Watch' : 'Effects'}
                    </p>
                    <p style={{ fontSize: '1.0625rem', lineHeight: 1.8 }}>{pred.effects}</p>
                  </div>

                  {pred.health_concerns && (
                    <div style={{ 
                      padding: '1.5rem', 
                      background: 'rgba(220, 38, 38, 0.2)', 
                      borderRadius: '12px', 
                      marginBottom: '1.5rem',
                      border: '2px solid #DC2626'
                    }}>
                      <p style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AlertTriangle size={20} />
                        HEALTH ALERT
                      </p>
                      <p style={{ fontSize: '1.0625rem', lineHeight: 1.8, color: '#F9FAFB' }}>{pred.health_concerns}</p>
                    </div>
                  )}

                  <div style={{ 
                    padding: '1.5rem', 
                    background: 'rgba(16, 185, 129, 0.1)', 
                    borderRadius: '12px',
                    border: '2px solid #10B981'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.25rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Heart size={22} />
                      Remedies for Protection
                    </h4>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {pred.remedies.map((remedy, j) => (
                        <li key={j} style={{
                          padding: '1rem 1.25rem',
                          background: 'rgba(16, 185, 129, 0.15)',
                          borderRadius: '10px',
                          marginBottom: '0.75rem',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '1rem',
                          borderLeft: '3px solid #10B981'
                        }}>
                          <Check size={20} color="#10B981" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <span style={{ fontSize: '1rem', lineHeight: 1.7 }}>{remedy}</span>
                        </li>
                      ))}
                    </ul>

                    {pred.beneficial_actions && (
                      <div style={{ 
                        marginTop: '1.25rem', 
                        padding: '1rem', 
                        background: 'rgba(59, 130, 246, 0.1)', 
                        borderRadius: '8px'
                      }}>
                        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#3B82F6', marginBottom: '0.5rem' }}>
                          RECOMMENDED ACTIONS:
                        </p>
                        <p style={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>{pred.beneficial_actions}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'lunar':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Lunar Astrology - Past Life & Karma</h2>
            
            {lunar_astrology?.soul_purpose && (
              <div style={{ 
                padding: '2rem', 
                background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.15) 0%, rgba(0, 217, 255, 0.05) 100%)',
                borderRadius: '16px',
                marginBottom: '2rem',
                border: '2px solid #00D9FF'
              }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#00D9FF' }}>Your Soul Purpose</h3>
                <p style={{ fontSize: '1.125rem', lineHeight: 1.8 }}>{lunar_astrology.soul_purpose}</p>
              </div>
            )}

            {lunar_astrology?.past_life_indicators && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Past Life Indicators</h3>
                {lunar_astrology.past_life_indicators.map((indicator, i) => (
                  <div key={i} style={{ 
                    padding: '1.5rem', 
                    background: '#1E2139', 
                    borderRadius: '12px',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', marginBottom: '0.75rem', color: '#7C3AED' }}>
                      {indicator.indicator}
                    </h4>
                    {indicator.nakshatra && (
                      <p style={{ fontSize: '1rem', color: '#A855F7', fontWeight: 600, marginBottom: '0.75rem' }}>
                        {indicator.nakshatra} {indicator.rashi && `in ${indicator.rashi}`}
                      </p>
                    )}
                    <p style={{ fontSize: '1rem', lineHeight: 1.7, marginBottom: '1rem' }}>
                      {indicator.meaning}
                    </p>
                    {indicator.karmic_lesson && (
                      <div style={{ padding: '1rem', background: 'rgba(124, 58, 237, 0.1)', borderRadius: '8px' }}>
                        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#7C3AED', marginBottom: '0.5rem' }}>
                          Karmic Lesson:
                        </p>
                        <p style={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>{indicator.karmic_lesson}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {lunar_astrology?.karmic_debts && (
              <div>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Karmic Debts</h3>
                {lunar_astrology.karmic_debts.map((debt, i) => (
                  <div key={i} style={{ 
                    padding: '1.5rem', 
                    background: '#1E2139', 
                    borderRadius: '12px'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>
                      {debt.planet} in {debt.position}
                    </h4>
                    <p style={{ fontSize: '1rem', lineHeight: 1.7, marginBottom: '1rem' }}>
                      {debt.life_lesson}
                    </p>
                    <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px' }}>
                      <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#10B981', marginBottom: '0.5rem' }}>
                        Remedies:
                      </p>
                      <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', margin: 0 }}>
                        {debt.remedies.map((remedy, j) => (
                          <li key={j} style={{ fontSize: '0.9375rem', marginBottom: '0.25rem', lineHeight: 1.6 }}>
                            {remedy}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'chat':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Ask Your Astrology Questions</h2>
            
            <div style={{ 
              height: '500px', 
              background: '#1E2139', 
              borderRadius: '12px', 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {chatMessages.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <p style={{ color: '#9CA3AF', marginBottom: '1.5rem' }}>Ask questions based on your Kundali:</p>
                    {['Will I go to foreign countries?', 'When will I get married?', 'What challenges are ahead?', 'Describe my spouse'].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => setChatInput(q)}
                        className="btn btn-ghost"
                        style={{ width: '100%', marginBottom: '0.5rem', textAlign: 'left' }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                {chatMessages.map((msg, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.75rem', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: msg.role === 'user' ? '#7C3AED' : 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '0.875rem',
                      fontWeight: 700
                    }}>
                      {msg.role === 'user' ? <User size={18} /> : <Bot size={18} />}
                    </div>
                    <div style={{
                      maxWidth: '75%',
                      padding: '1rem 1.25rem',
                      borderRadius: '12px',
                      background: msg.role === 'user' ? '#7C3AED' : '#0A0F2A',
                      fontSize: '1rem',
                      lineHeight: 1.7
                    }}>
                      {msg.content}
                    </div>
                  </div>
                ))}

                {chatLoading && (
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Bot size={18} />
                    </div>
                    <div style={{ padding: '1rem', background: '#0A0F2A', borderRadius: '12px', color: '#9CA3AF' }}>
                      Analyzing your Kundali...
                    </div>
                  </div>
                )}
                
                <div ref={chatEndRef} />
              </div>

              <div style={{ padding: '1.5rem', borderTop: '1px solid #2D3748' }}>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                    placeholder="Ask about marriage, career, foreign travel..."
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: '#0A0F2A',
                      border: '1px solid #374151',
                      borderRadius: '12px',
                      color: '#F9FAFB',
                      fontSize: '1rem'
                    }}
                  />
                  <button
                    onClick={handleChatSend}
                    disabled={!chatInput.trim() || chatLoading}
                    className="btn btn-primary"
                    style={{ padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0A0F2A', flexDirection: window.innerWidth < 768 ? 'column' : 'row' }}>
      {/* Mobile Header with Menu Button */}
      {window.innerWidth < 768 && (
        <div style={{ 
          background: '#151827', 
          padding: '1rem', 
          borderBottom: '2px solid #2D3748',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-ghost"
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            <ArrowLeft size={18} style={{ marginRight: '0.25rem' }} />
            Back
          </button>
          <h2 style={{ 
            fontSize: '1.25rem',
            background: 'linear-gradient(90deg, #7C3AED 0%, #A855F7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            margin: 0
          }}>
            Astrology
          </h2>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="btn btn-ghost"
            style={{ padding: '0.5rem 1rem' }}
          >
            <Menu size={24} />
          </button>
        </div>
      )}

      {/* Sidebar - Desktop or Mobile Overlay */}
      <div style={{ 
        width: window.innerWidth < 768 ? '100%' : '280px',
        background: '#151827', 
        borderRight: window.innerWidth < 768 ? 'none' : '2px solid #2D3748',
        padding: window.innerWidth < 768 ? '1rem' : '2rem 1rem',
        position: window.innerWidth < 768 ? 'fixed' : 'sticky',
        top: window.innerWidth < 768 ? '60px' : 0,
        height: window.innerWidth < 768 ? 'auto' : '100vh',
        overflowY: 'auto',
        zIndex: 90,
        display: window.innerWidth < 768 ? (mobileMenuOpen ? 'block' : 'none') : 'block',
        maxHeight: window.innerWidth < 768 ? 'calc(100vh - 60px)' : '100vh',
        boxShadow: window.innerWidth < 768 && mobileMenuOpen ? '0 4px 20px rgba(0,0,0,0.5)' : 'none'
      }}>
        {window.innerWidth >= 768 && (
          <>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-ghost"
              style={{ width: '100%', marginBottom: '2rem', justifyContent: 'flex-start' }}
            >
              <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
              Dashboard
            </button>

            <h2 style={{ 
              fontSize: '1.5rem', 
              marginBottom: '1.5rem',
              background: 'linear-gradient(90deg, #7C3AED 0%, #A855F7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              Astrology
            </h2>
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {MENU_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActivePanel(item.id);
                if (window.innerWidth < 768) setMobileMenuOpen(false);
              }}
              className={activePanel === item.id ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ 
                justifyContent: 'flex-start', 
                padding: window.innerWidth < 768 ? '0.75rem' : '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                fontSize: window.innerWidth < 768 ? '0.9375rem' : '1rem'
              }}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: window.innerWidth < 768 ? '1rem' : '2rem', overflowY: 'auto', width: window.innerWidth < 768 ? '100%' : 'auto' }}>
        <motion.div
          key={activePanel}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderPanelContent()}
        </motion.div>
      </div>
    </div>
  );
}

export default AstrologyDashboard;
